import { useState, useEffect, useContext, useMemo } from "react";
import api from '../api/axios';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../context/LanguageContext";
import { UserContext } from "../context/UserContext";
import { PageContainer, PageTitleBadge, ModernButton, DataGridTable, SearchBar, FilterSelect, FiltersContainer } from "../components";
import { FaPlus, FaDna, FaEdit } from "react-icons/fa";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import "../styles/common.css";
import "./Reproductions.css";

export default function Reproductions() {
  const { user } = useContext(UserContext);
  const { t } = useTranslation();
  const [reproductions, setReproductions] = useState([]);
  const [animaux, setAnimaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtres, setFiltres] = useState({
    statut: '',
    mere: '',
    pere: '',
  });
  const [searchTerm, setSearchTerm] = useState('');

  const navigate = useNavigate();

  // État pour le zoom (gardé pour les colonnes qui utilisent zoomLevel dans leurs styles)
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('reproductions-zoom-level');
    return saved ? parseFloat(saved) : 1.0;
  });

  useEffect(() => {
    localStorage.setItem('reproductions-zoom-level', zoomLevel.toString());
  }, [zoomLevel]);

  useEffect(() => {
    const fetchReproductions = async () => {
      setLoading(true);
      setError(null);
      try {
        const animauxResponse = await api.get('/api/animaux/');
        setAnimaux(animauxResponse.data);
        const params = new URLSearchParams();
        if (filtres.statut) params.append('statut', filtres.statut);
        if (filtres.mere) params.append('mere', filtres.mere);
        if (filtres.pere) params.append('pere', filtres.pere);
        if (searchTerm) params.append('search', searchTerm);
        const response = await api.get(`/api/reproductions/`, { params });
        setReproductions(response.data);
      } catch (err) {
        setError("Erreur lors du chargement des reproductions.");
      } finally {
        setLoading(false);
      }
    };
    fetchReproductions();
  }, [filtres, searchTerm]);

  const handleFiltreChange = (e) => {
    setFiltres({
      ...filtres,
      [e.target.name]: e.target.value,
    });
  };


  // Préparer les données pour DataGrid
  const rows = useMemo(() => {
    return reproductions.map(repro => {
      const mereAnimal = animaux.find(a => a.id === repro.mere);
      const pereAnimal = animaux.find(a => a.id === repro.pere);
      return {
        id: repro.id,
        mere: mereAnimal ? mereAnimal.matricule : repro.mere,
        pere: pereAnimal ? pereAnimal.matricule : repro.pere,
        date_chaleur: repro.date_chaleur || 'N/A',
        date_saillie: repro.date_saillie || 'N/A',
        date_velage: repro.date_velage || 'N/A',
        nb_veaux: repro.nb_veaux || 0,
        statut: repro.statut,
        notes: repro.notes || 'N/A',
        mereId: repro.mere,
        pereId: repro.pere,
      };
    });
  }, [reproductions, animaux]);

  // Configuration des colonnes pour DataGrid
  const baseColumns = useMemo(() => [
    {
      field: 'mere',
      headerName: 'Génitrice',
      width: 150,
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'pere',
      headerName: 'Géniteur',
      width: 150,
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'date_chaleur',
      headerName: 'Chaleur',
      width: 130,
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'date_saillie',
      headerName: 'Saillie',
      width: 130,
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'date_velage',
      headerName: 'Vêlage',
      width: 130,
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'nb_veaux',
      headerName: 'Veau/Velle',
      width: 120,
      flex: 1,
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'statut',
      headerName: 'Statut',
      width: 130,
      flex: 1,
      minWidth: 100,
      renderCell: (params) => {
        const statut = params.value;
        const statutClass = statut === 'termine' ? 'status-actif' : 
                           statut === 'en_cours' ? 'status-NonTraite' : 
                           'status-mort';
        return (
          <span className={`status-badge ${statutClass}`}>
            {statut === 'termine' ? 'Terminé' : 
             statut === 'en_cours' ? 'En cours' : 
             statut === 'echec' ? 'Échec' : statut}
          </span>
        );
      },
    },
    {
      field: 'notes',
      headerName: 'Notes',
      width: 200,
      flex: 1,
      minWidth: 150,
    },
  ], []);

  const adminColumns = useMemo(() => {
    const canEdit = user?.is_admin || hasPermission(user, PERMISSIONS.CHANGE_REPRODUCTION);
    if (!canEdit) return [];
    
    return [
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <button
            onClick={() => navigate(`/reproductions/${params.row.id}/modifier`)}
            className="action-button"
          >
            <FaEdit />
            <span>Modifier</span>
          </button>
        ),
      },
    ];
  }, [user, navigate]);

  const columns = useMemo(() => {
    return adminColumns.length > 0 ? [...baseColumns, ...adminColumns] : baseColumns;
  }, [baseColumns, adminColumns]);

  if (loading) {
    return (
      <PageContainer>
        <div className="loading-container">
          <p className="loading-text">Chargement des reproductions...</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageTitleBadge title="ERREUR" icon={<FaDna />} />
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitleBadge title={t.reproductionManagement} icon={<FaDna />} />

      {/* Bouton d'action admin */}
      {(user?.is_admin || hasPermission(user, PERMISSIONS.ADD_REPRODUCTION)) && (
        <div className="action-buttons-container">
          <ModernButton
            variant="primary"
            icon={<FaPlus />}
            onClick={() => navigate("/ajouter-reproduction")}
          >
            {t.addReproduction}
          </ModernButton>
        </div>
      )}

      {/* Barre de recherche */}
      <div className="top-section">
        <SearchBar
          placeholder={t.searchByReproduction}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="default"
        />
      </div>

      {/* Filtres */}
      <FiltersContainer title={t.filters}>
        <FilterSelect
          name="statut"
          value={filtres.statut}
          onChange={handleFiltreChange}
          options={[
            { value: "en_cours", label: t.inProgress },
            { value: "termine", label: t.completed },
            { value: "echec", label: t.failed }
          ]}
          placeholder={t.filterByStatus}
          className="filtre-statut"
          width="180px"
        />
        <FilterSelect
          name="mere"
          value={filtres.mere}
          onChange={handleFiltreChange}
          options={animaux
            .filter((a) => a.sexe === "F")
            .map((a) => ({ id: a.id, nom: a.matricule }))}
          placeholder={t.filterByDam}
          className="filtre-mere"
          width="200px"
        />
        <FilterSelect
          name="pere"
          value={filtres.pere}
          onChange={handleFiltreChange}
          options={animaux
            .filter((a) => a.sexe === "M")
            .map((a) => ({ id: a.id, nom: a.matricule }))}
          placeholder={t.filterBySire}
          className="filtre-pere"
          width="200px"
        />
      </FiltersContainer>

      {/* DataGrid */}
      {reproductions.length === 0 ? (
        <div className="empty-message-container">
          <p className="empty-message">Aucune reproduction enregistrée ne correspond aux critères.</p>
        </div>
      ) : (
        <DataGridTable
          rows={rows}
          columns={columns}
          loading={loading}
          storageKey="reproductions-zoom-level"
          zoomLevel={zoomLevel}
          onZoomChange={setZoomLevel}
          initialSort={{ field: 'date_chaleur', sort: 'desc' }}
          pageSize={25}
          containerClassName="reproductions-grid-container"
        />
      )}
    </PageContainer>
  );
}
